import React, { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Flights, PlaneIco } from '../../assests/ExportImageIcon'
import DatePicker from '../../componants/datePicker/DatePicker'
import AirlineAutocomplete from '../../componants/airlineAutocomplete/AirlineAutocomplete'
import service from '../../services/service'
import endpoints from '../../services/endpoints'
import { showToast } from '../../componants/toast/toastService'
import './flightTrack.css'

const todayIso = () => new Date().toISOString().split('T')[0]

const FieldError = ({ children }) => (
    children ? <p className='hotelGuestFieldError'>{children}</p> : null
)

const RefreshIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M23 4v6h-6M1 20v-6h6" stroke="#026D4D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3.51 9a9 9 0 0 1 14.13-3.36L23 10M1 14l5.36 4.36A9 9 0 0 0 20.49 15" stroke="#026D4D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

// "FRIDAY 18-SEP-2026" — matches the flight-status reference's date style.
const formatFlightDate = (value) => {
    if (!value) return ''
    const date = new Date(`${value}T00:00:00`)
    if (Number.isNaN(date.getTime())) return value
    const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' }).toUpperCase()
    const day = date.toLocaleDateString('en-GB', { day: '2-digit' })
    const month = date.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase()
    return `${weekday} ${day}-${month}-${date.getFullYear()}`
}

const formatMinutesLabel = (minutes) => {
    if (minutes == null || Number.isNaN(minutes)) return ''
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    return [h ? `${h}h` : '', m ? `${m}m` : ''].filter(Boolean).join(' ') || '0m'
}

// delayMinutes is negative when the leg is running ahead of schedule —
// delayLabel (when the API sends one) always wins over the computed guess.
const legDelta = (leg) => {
    if (leg?.delayLabel) return { label: leg.delayLabel, late: true }
    const mins = Number(leg?.delayMinutes)
    if (!mins) return { label: 'On time', late: false }
    if (mins > 0) return { label: `Delayed by ${mins} min`, late: true }
    return { label: `${Math.abs(mins)} min early`, late: false }
}

// Matches the backend's STATUS_MAP exactly (scheduled/active/landed/
// cancelled/diverted/unknown) — the badge's color always follows this real
// lifecycle status, not the separate per-leg delay-minutes flag below.
const KNOWN_STATUS_KINDS = ['scheduled', 'active', 'landed', 'cancelled', 'diverted']

// Maps the real GET /flights/status response into the shape this page renders.
const mapFlightStatus = (raw) => {
    const elapsedMinutes = raw?.totalTravel?.minutes != null && raw?.remainingMinutes != null
        ? Math.max(0, raw.totalTravel.minutes - raw.remainingMinutes)
        : null
    const isDelayed = Number(raw?.departure?.delayMinutes) > 0 || Number(raw?.arrival?.delayMinutes) > 0
    const rawStatusKind = String(raw?.status || raw?.statusLabel || '').toLowerCase()
    const statusKind = KNOWN_STATUS_KINDS.includes(rawStatusKind) ? rawStatusKind : 'unknown'

    return {
        airline: raw?.airline || '',
        flightNumber: raw?.flightNumber || '',
        callsign: `${raw?.icao || ''}${raw?.flightNumber || ''}`,
        iataCallsign: `${raw?.airlineCode || ''}${raw?.flightNumber || ''}`,
        tailNumber: raw?.tailNumber || '',
        image: raw?.image || '',
        aircraft: raw?.aircraft || '',
        statusLabel: raw?.statusLabel || raw?.status || '',
        isDelayed,
        statusKind,
        etaLabel: raw?.remainingLabel ? `Arriving in ${raw.remainingLabel}` : '',
        departure: {
            code: raw?.departure?.iata || '',
            city: raw?.departure?.city || '',
            airportName: raw?.departure?.airportName || '',
            date: formatFlightDate(raw?.departure?.dateLocal),
            time: raw?.departure?.timeLocal || '',
            delta: legDelta(raw?.departure),
        },
        arrival: {
            code: raw?.arrival?.iata || '',
            city: raw?.arrival?.city || '',
            airportName: raw?.arrival?.airportName || '',
            date: formatFlightDate(raw?.arrival?.dateLocal),
            time: raw?.arrival?.timeLocal || '',
            delta: legDelta(raw?.arrival),
            terminal: raw?.arrivalTerminal || '',
            baggageBelt: raw?.baggageBelt || '',
        },
        elapsedLabel: elapsedMinutes != null ? `${formatMinutesLabel(elapsedMinutes)} elapsed` : '',
        remainingLabel: raw?.remainingLabel ? `${raw.remainingLabel} remaining` : '',
        totalTravelTime: raw?.totalTravel?.label || '',
        progressPercent: Math.max(0, Math.min(100, Number(raw?.progressPercent) || 0)),
        altitudeFt: raw?.track?.altitudeFt ?? null,
        speedMph: raw?.track?.speedMph ?? null,
        track: (raw?.track?.lat != null && raw?.track?.lon != null) ? {
            lat: raw.track.lat,
            lon: raw.track.lon,
            heading: raw.track.heading ?? null,
            // Sort oldest-to-newest by actual timestamp (not just reversed
            // array order) so the polyline always draws in true chronological
            // sequence — a plain .reverse() would still zigzag if the API's
            // own ordering has any out-of-sequence points.
            positions: Array.isArray(raw.track.positions)
                ? [...raw.track.positions]
                    .filter((p) => p?.lat != null && p?.lon != null)
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                : [],
        } : null,
    }
}

const FlightTrack = () => {
    const [airline, setAirline] = useState(null)
    const [flightNumber, setFlightNumber] = useState('')
    const [date, setDate] = useState(todayIso())
    const [submitAttempted, setSubmitAttempted] = useState(false)
    const [loading, setLoading] = useState(false)
    const [result, setResult] = useState(null)
    const mapContainerRef = useRef(null)
    const mapInstanceRef = useRef(null)

    // Leaflet + OpenStreetMap tiles (no API key needed) — draws the flown
    // path as a polyline and a rotated plane marker at the current position.
    // The map section only renders while a result with track data exists, so
    // it has to be (re)built each time a new result lands and torn down when
    // it's cleared or replaced.
    useEffect(() => {
        if (!result?.track || !mapContainerRef.current) return undefined

        const { lat, lon, heading, positions } = result.track
        const map = L.map(mapContainerRef.current).setView([lat, lon], 7)
        mapInstanceRef.current = map

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors',
        }).addTo(map)

        const pathPoints = positions.length ? positions.map((p) => [p.lat, p.lon]) : []
        const lastPoint = pathPoints[pathPoints.length - 1]
        // Skip appending the current position if it's already the last
        // tracked point (common — the API's most recent position report
        // often matches the top-level "current" coords exactly).
        const needsCurrentPoint = !lastPoint || lastPoint[0] !== lat || lastPoint[1] !== lon
        if (pathPoints.length > 1) {
            const fullPath = needsCurrentPoint ? [...pathPoints, [lat, lon]] : pathPoints
            const polyline = L.polyline(fullPath, { color: '#026D4D', weight: 3, dashArray: '2, 10', lineCap: 'round' }).addTo(map)
            map.fitBounds(polyline.getBounds(), { padding: [30, 30] })

            const startIcon = L.divIcon({
                className: 'flightTrackResult__mapPlaneIcon',
                html: '<svg width="26" height="26" viewBox="0 0 20 20" fill="none"><path d="M10 0C5.58 0 2 3.58 2 8c0 6 8 12 8 12s8-6 8-12c0-4.42-3.58-8-8-8z" fill="#E23744"/></svg>',
                iconSize: [26, 26],
                iconAnchor: [13, 26],
            })
            L.marker(fullPath[0], { icon: startIcon }).addTo(map)
        }

        const planeIcon = L.divIcon({
            className: 'flightTrackResult__mapPlaneIcon',
            html: `<img src="${PlaneIco}" style="width:24px;height:24px;transform:rotate(${(heading ?? 90) - 90}deg);" />`,
            iconSize: [24, 24],
            iconAnchor: [12, 12],
        })
        L.marker([lat, lon], { icon: planeIcon }).addTo(map)

        return () => {
            map.remove()
            mapInstanceRef.current = null
        }
    }, [result])

    const errors = {
        airline: submitAttempted && !airline,
        flightNumber: submitAttempted && !flightNumber.trim(),
        date: submitAttempted && !date,
    }

    const handleSearch = () => {
        setSubmitAttempted(true)
        if (!airline || !flightNumber.trim() || !date) {
            showToast('Please fill in all fields to search', 'error')
            return
        }
        setLoading(true)
        service
            .get(endpoints.flights.status, {
                airline: airline.airline_code,
                flightNumber: flightNumber.trim(),
                arrivalDate: date,
            })
            .then(({ data }) => setResult(mapFlightStatus(data.data)))
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setLoading(false))
    }

    return (
        <>
            <section className='trackingBanner'>
                <div className='container'>
                    <div className='trackingBanner__inner'>
                        <div className='trackingBanner__content'>
                            <h2 className='trackingBanner__title'>Real-Time Flight Tracking</h2>
                            <p className='trackingBanner__subtitle'>
                                Live status, delays for flights around the world — right when they happen.
                            </p>
                            <div className='trackingBanner__features'>
                                <span className='trackingBanner__feature'>Live Status Updates</span>
                                {/* <span className='trackingBanner__feature'>Gate &amp; Terminal Info</span> */}
                                <span className='trackingBanner__feature'>Live Map Tracking</span>
                            </div>
                        </div>
                        <div className='trackingBanner__graphic'>
                            <img src={PlaneIco} alt='' />
                        </div>
                    </div>
                </div>
            </section>
            <section className='flightTrackSearch pb-4'>
                <div className='container'>
                    {/* <div className='flightTrackSearch__head'>
                        <h1 className='flightTrackSearch__title'>Flight Status</h1>
                        <p className='flightTrackSearch__subtitle'>Track any flight by number, in real time</p>
                    </div> */}
                    <div className='flightTrackSearch__card'>
                        <div className='flightTrackSearch__row'>
                            <div className='flightTrackSearch__field flightTrackSearch__field--airline'>
                                <label>Airline</label>
                                <AirlineAutocomplete
                                    className={errors.airline ? 'travellerField__input--error' : ''}
                                    value={airline}
                                    onChange={setAirline}
                                />
                                <FieldError>{errors.airline ? 'Airline is required' : ''}</FieldError>
                            </div>
                            <div className='flightTrackSearch__field'>
                                <label>Flight number</label>
                                <input
                                    type='text'
                                    placeholder='Example: 200'
                                    className={errors.flightNumber ? 'travellerField__input--error' : ''}
                                    value={flightNumber}
                                    onChange={(e) => setFlightNumber(e.target.value)}
                                />
                                <FieldError>{errors.flightNumber ? 'Flight number is required' : ''}</FieldError>
                            </div>
                            <div className='flightTrackSearch__field'>
                                <label>Date</label>
                                <DatePicker
                                    className={errors.date ? 'travellerField__input--error' : ''}
                                    value={date}
                                    onChange={setDate}
                                />
                                <FieldError>{errors.date ? 'Date is required' : ''}</FieldError>
                            </div>
                            <button type='button' className='flightTrackSearch__searchBtn' onClick={handleSearch} disabled={loading}>
                                <svg width="16" height="16" viewBox="0 0 17 17" fill="none">
                                    <path d="M15.7501 15.7501L12.1335 12.1335M14.0833 7.41667C14.0833 11.0986 11.0986 14.0833 7.41667 14.0833C3.73477 14.0833 0.75 11.0986 0.75 7.41667C0.75 3.73477 3.73477 0.75 7.41667 0.75C11.0986 0.75 14.0833 3.73477 14.0833 7.41667Z" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
                                </svg>
                                {loading ? 'Searching...' : 'Search'}
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {result && (
                <section className='flightTrackResult pb-4 pt-0'>
                    <div className='container'>
                        <div className='resultCard flightTrackResult__card'>
                            <div className='flightTrackResult__header'>
                                <div className='flightTrackResult__headerLeft'>
                                    <span className={`flightTrackResult__logo ${result.image ? 'flightTrackResult__logo--photo' : ''}`}>
                                        <img
                                            src={result.image || PlaneIco}
                                            alt=''
                                            onError={(e) => {
                                                if (e.currentTarget.src !== PlaneIco) e.currentTarget.src = PlaneIco
                                            }}
                                        />
                                    </span>
                                    <div>
                                        <div className='flightTrackResult__airlineName'>{result.airline} {result.flightNumber}</div>
                                        <div className='flightTrackResult__callsign'>{result.callsign} / {result.iataCallsign}</div>
                                        {result.tailNumber && <div className='flightTrackResult__note'>Tail Number: {result.tailNumber}</div>}
                                    </div>
                                </div>
                                <div className='flightTrackResult__headerRight'>
                                    <span className={`flightTrackResult__statusBadge flightTrackResult__statusBadge--${result.statusKind}`}>
                                        {result.statusLabel}
                                    </span>
                                    {result.etaLabel && (
                                        <div className={`flightTrackResult__etaLine ${result.isDelayed ? 'flightTrackResult__etaLine--late' : ''}`}>
                                            {result.etaLabel}
                                        </div>
                                    )}
                                    <button
                                        type='button'
                                        className={`flightTrackResult__refreshBtn ${loading ? 'flightTrackResult__refreshBtn--loading' : ''}`}
                                        onClick={handleSearch}
                                        disabled={loading}
                                        aria-label='Refresh flight status'
                                    >
                                        <RefreshIcon />
                                    </button>
                                </div>
                            </div>

                            <div className='bsFareDivider mt-3' />

                            <div className='flightTrackResult__legRow mt-3'>
                                <div className='flightTrackResult__leg'>
                                    <div className='flightTrackResult__legCode'>{result.departure.code}</div>
                                    <div className='flightTrackResult__legCity'>{result.departure.city}</div>
                                    <div className='flightTrackResult__note mt-2'>{result.departure.airportName} · {result.departure.code}</div>
                                    <div className='flightTrackResult__legDate'>{result.departure.date}</div>
                                    <div className='flightTrackResult__legTime'>
                                        {result.departure.time}
                                        <span className={`flightTrackResult__delta ${result.departure.delta.late ? 'flightTrackResult__delta--late' : 'flightTrackResult__delta--ontime'}`}>
                                            ({result.departure.delta.label})
                                        </span>
                                    </div>
                                </div>
                                <div className='flightTrackResult__leg flightTrackResult__leg--end'>
                                    <div className='flightTrackResult__legCode'>{result.arrival.code}</div>
                                    <div className='flightTrackResult__legCity'>{result.arrival.city}</div>
                                    <div className='flightTrackResult__note mt-2'>{result.arrival.airportName} · {result.arrival.code}</div>
                                    <div className='flightTrackResult__legDate'>{result.arrival.date}</div>
                                    <div className='flightTrackResult__legTime'>
                                        <span className={`flightTrackResult__delta ${result.arrival.delta.late ? 'flightTrackResult__delta--late' : 'flightTrackResult__delta--ontime'}`}>
                                            ({result.arrival.delta.label})
                                        </span>
                                        {result.arrival.time}
                                    </div>
                                    {result.arrival.terminal && (
                                        <div className='flightTrackResult__legMeta'>Terminal {result.arrival.terminal}</div>
                                    )}
                                    {result.arrival.baggageBelt && (
                                        <div className='flightTrackResult__legMeta'>Baggage Belt {result.arrival.baggageBelt}</div>
                                    )}
                                </div>
                            </div>

                            {(result.elapsedLabel || result.remainingLabel) && (
                                <div className='flightTrackResult__timeline mt-3'>
                                    <div className='flightTrackResult__timelineTrack'>
                                        <span className='flightTrackResult__timelineDot' />
                                        <span className='flightTrackResult__timelineBar'>
                                            <span className='flightTrackResult__progressFill' style={{ width: `${result.progressPercent}%` }} />
                                        </span>
                                        <span className='flightTrackResult__progressPlane' style={{ left: `${result.progressPercent}%` }}>
                                            <img src={PlaneIco} alt='' />
                                        </span>
                                        <span className={`flightTrackResult__timelineDot flightTrackResult__timelineDot--end ${result.progressPercent >= 100 ? 'flightTrackResult__timelineDot--complete' : ''}`} />
                                    </div>
                                    <div className='flightTrackResult__timelineLabels'>
                                        {result.elapsedLabel && <span className='flightTrackResult__timelineBadge'>{result.elapsedLabel}</span>}
                                        {result.totalTravelTime && <span className='flightTrackResult__timelineTotal'>{result.totalTravelTime} total travel time</span>}
                                        {result.remainingLabel && <span className='flightTrackResult__timelineBadge'>{result.remainingLabel}</span>}
                                    </div>
                                </div>
                            )}

                            {(result.aircraft || result.altitudeFt != null || result.speedMph != null) && (
                                <>
                                    <div className='bsFareDivider mt-3' />
                                    <p className='flightTrackResult__footer'>
                                        {[
                                            result.aircraft,
                                            result.altitudeFt != null ? `Altitude ${Number(result.altitudeFt).toLocaleString('en-IN')} ft` : null,
                                            result.speedMph != null ? `Speed ${Math.round(result.speedMph)} mph` : null,
                                        ].filter(Boolean).join(' · ')}
                                    </p>
                                </>
                            )}

                        </div>

                        {result.track && (
                            <div className='resultCard flightTrackResult__card flightTrackResult__mapCard mt-3'>
                                <div className='flightTrackResult__mapHead'>
                                    <div className='bsSectionTitle mb-0'>Live Position — {result.airline} {result.flightNumber}</div>
                                    <button
                                        type='button'
                                        className={`flightTrackResult__refreshBtn ${loading ? 'flightTrackResult__refreshBtn--loading' : ''}`}
                                        onClick={handleSearch}
                                        disabled={loading}
                                        aria-label='Refresh live position'
                                    >
                                        {/* <RefreshIcon /> */}
                                    </button>
                                </div>
                                <div className='flightTrackResult__mapWrap' ref={mapContainerRef} />
                                <p className='flightTrackResult__footer mt-2'>
                                    {[
                                        result.altitudeFt != null ? `Altitude ${Number(result.altitudeFt).toLocaleString('en-IN')} ft` : null,
                                        result.speedMph != null ? `Speed ${Math.round(result.speedMph)} mph` : null,
                                        result.track.heading != null ? `Heading ${Math.round(result.track.heading)}°` : null,
                                    ].filter(Boolean).join(' · ')}
                                </p>
                            </div>
                        )}
                    </div>
                </section>
            )}
        </>
    )
}

export default FlightTrack
